import React, { useRef } from 'react';
import { MIN_COLUMN_WIDTH_PX, clampColumnWidthPx } from '../features/quotations/gridLayout';

interface ColumnResizeGripProps {
  /** The width the drag has reached, on every mouse move: whole pixels, never under the floor. */
  onResize: (px: number) => void;
  /** Double-click: put this column back to its default. */
  onReset: () => void;
  onResizeStart?: () => void;
  /** Once per drag, on mouse release: where a resize is saved. */
  onResizeEnd?: () => void;
}

/**
 * The resize grip on a column's right edge, for the quotation grid and the tables (D178).
 * It must be a DIRECT child of the (positioned) header cell: the drag starts from that cell's width.
 */
export const ColumnResizeGrip: React.FC<ColumnResizeGripProps> = ({ onResize, onReset, onResizeStart, onResizeEnd }) => {
  const dragging = useRef(false);

  const beginResize = (event: React.MouseEvent<HTMLElement>) => {
    // `stopPropagation` is what stops the grip from also starting a draggable header's reorder.
    event.preventDefault();
    event.stopPropagation();
    const cell = event.currentTarget.parentElement;
    const startWidth = cell ? cell.getBoundingClientRect().width : MIN_COLUMN_WIDTH_PX;
    const startX = event.clientX;
    dragging.current = true;
    onResizeStart?.();

    // Pinned on the BODY: dragging fast enough to leave the 6px grip would flip the
    // cursor back and start selecting the header text under the pointer.
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMove = (e: MouseEvent) => {
      if (dragging.current) onResize(clampColumnWidthPx(startWidth + (e.clientX - startX)));
    };
    const onUp = () => {
      dragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      onResizeEnd?.();
    };

    // On WINDOW, not the grip: the pointer leaves a 6px target immediately,
    // and a listener on the grip would drop the drag the moment it did.
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  return (
    <span
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize column"
      title="Drag to resize · double-click to reset this column"
      onMouseDown={beginResize}
      onDoubleClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onReset();
      }}
      onDragStart={(e) => e.preventDefault()}
      className="absolute top-0 right-0 h-full w-[6px] cursor-col-resize hover:bg-primary-500/30 active:bg-primary-500/50 z-20"
    />
  );
};

export default ColumnResizeGrip;
